<template>
  <v-container>
    <v-row no-gutters class="wizard__content">
      <v-col cols="12">
        <v-form
          v-model="valid"
          :class="{ 'mt-5': $vuetify.breakpoint.smAndUp }"
        >
          <v-row justify="center" class="mt-5">
            <v-col cols="6">
              <h3 v-if="showTitleAndDescriptionTexts" class="mb-3">
                {{ $t("message.common.freeTrialTitle", [trialDuration]) }}
              </h3>
              <v-card outlined class="pa-5">
                <v-text-field
                  v-for="item in [form.name]"
                  :key="item.id"
                  v-model="item.value"
                  v-validate="{ required: item.required }"
                  :data-vv-name="$t(item.label)"
                  :error-messages="errors.collect($t(item.label))"
                  :label="
                    item.required ? `${$t(item.label)} *` : $t(item.label)
                  "
                  :required="item.required"
                  :prefix="item.prefix"
                ></v-text-field>
                <v-select
                  v-model="form.country_code.value"
                  v-validate="{ required: true }"
                  :items="countries"
                  :data-vv-name="$t(form.country_code.label)"
                  :error-messages="errors.collect($t(form.country_code.label))"
                  :label="
                    form.country_code.required
                      ? `${$t(form.country_code.label)} *`
                      : $t(form.country_code.label)
                  "
                  :required="form.country_code.required"
                  item-text="name"
                  item-value="code"
                ></v-select>
              </v-card>
              <p v-if="showTitleAndDescriptionTexts" class="trial-info">
                {{ $t("message.common.freeTrialInfo", [trialDuration]) }}
              </p>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col>
        <Footer>
          <ui-button color="primary" @click="submit">
            {{ $t("message.common.further") }}
          </ui-button>
        </Footer>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Footer from "@/components/Wizard/Footer.vue";
import serviceHandler from "@/helpers/handlers/service";
import companyService from "@/services/company";

import { AccountType } from "@/enums/account-type";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { LOCALE } from "@/enums/locale";
import PaymentsService from "@/services/payments";
import { companyProfileMixin } from "@/helpers/mixins/company-profile";
import { freeTrialDaysMixin } from "@/helpers/mixins/free-trial-days";
import { setCompanyMixin } from "@/helpers/mixins/set-company";
import usersService from "@/services/users";
import { checkUserType } from "@/helpers/user-type-check";
import moment from "moment";
import { getCookieValue } from "@/helpers/helpers";
import { LOCAL_STORAGE_KEYS } from "@/enums/local-storage";

export default {
  name: "WizardCompanyProfile",
  mixins: [companyProfileMixin, freeTrialDaysMixin, setCompanyMixin],
  components: {
    Footer
  },
  data() {
    return {
      valid: false,
      validTaxNumber: false,
      companyId: null,
      taxNumberDataLoading: false,
      dataInputType: "manually",
      initialPlanPayload: null,
      campaign: "",
      enterpriseInvitation: "",
      userId: null,
      subscriptionCreatedAt: null
    };
  },
  computed: {
    isFreeTrial() {
      return (
        this.$route.query.is_free_trial !== "false" &&
        !this.enterpriseInvitation
      );
    },
    isEnLanguage() {
      return this.$i18n.locale === LOCALE.EN;
    },
    showTitleAndDescriptionTexts() {
      return this.isFreeTrial && this.$route.query.action !== "add";
    }
  },
  async created() {
    this.form.country_code.value = null;
    this.campaign = this.$route.query.campaign;
    await this.getEnterpriseInvitation();
    this.setInitialPayload();
    this.$store.commit("company/SET_LOADING", true);
    await this.fetchCompanyProfileData();
    await this.fetchCountries();
    if (this.initialPlanPayload && this.initialPlanPayload.country_code) {
      this.setDefaultCountryCode(this.initialPlanPayload.country_code);
    } else if (
      this.$route.query.currency &&
      this.$route.query.currency.toLowerCase() === "pln"
    ) {
      this.setDefaultCountryCode("pl");
    }
    this.$store.commit("company/SET_LOADING", false);
  },

  async mounted() {
    this.form.email.value = this.$store.state.auth.user.email;
  },

  methods: {
    setDefaultCountryCode(countryCode) {
      const isLangOnList = this.countries.some(
        country => country.code.toLowerCase() === countryCode.toLowerCase()
      );
      if (isLangOnList) {
        this.form.country_code.value = countryCode.toUpperCase();
      }
    },
    async getEnterpriseInvitation() {
      this.enterpriseInvitation = this.$route.query.enterprise_invitation;
      if (!this.enterpriseInvitation) {
        try {
          const data = await serviceHandler(
            companyService.getEnterpriseInvitation()
          );
          this.enterpriseInvitation = data.enterprise_invitation;
        } catch {
          this.enterpriseInvitation = null;
        }
      }
    },
    submit() {
      this.$store.commit("company/SET_LOADING", true);

      this.$validator.validate().then(async isTrue => {
        if (isTrue) {
          this.$gtm.trackCustomEvent({
            event: GTM_EVENT_NAME.ONBOARDING_COMPANY_DATA_PROVIDED,
            company_name: this.form.name.value,
            country_code: this.form.country_code.value,
            ...(this.$route.query.country_code && {
              country_code_external: this.$route.query.country_code
            })
          });
          this.$gtm.trackCustomEvent({
            event: GTM_EVENT_NAME.WIZARD_STEP_1_COMPANY_ACTION,
            data_input_type: this.dataInputType
          });
          await this.send();
        }
        this.$store.commit("company/SET_LOADING", false);
      });
    },

    idFromObj(val) {
      return val.map(item => item.id || item);
    },
    async postInitialChosenPlan(payload) {
      await PaymentsService.postInitialChosenPlan(payload);
    },
    setInitialPayload() {
      const {
        payment_cycle: period,
        plan,
        locations: quantity,
        campaign: campaignIdentifier,
        country_code: countryCode,
        price_list: priceListId
      } = this.$route.query;
      if (period && plan && quantity && priceListId) {
        const PERIOD_ENUM = {
          monthly: 1,
          yearly: 2
        };
        this.initialPlanPayload = {
          ...(period && { period: PERIOD_ENUM[period] }),
          ...(plan && { product_name: plan }),
          ...(quantity && { quantity: +quantity }),
          ...(campaignIdentifier && {
            campaign: { identifier: campaignIdentifier }
          }),
          ...(countryCode && { country_code: countryCode }),
          ...(priceListId && { price_list: priceListId })
        };

        delete this.$route.query.payment_cycle;
        delete this.$route.query.plan;
        delete this.$route.query.locations;
        delete this.$route.query.campaign;
        delete this.$route.query.country_code;
        delete this.$route.query.price_list_id;

        this.$router.replace({
          query: {
            ...this.$route.query
          }
        });
      } else {
        this.initialPlanPayload = null;
      }
    },

    async send() {
      const formValues = {};
      this.form.categories.value = this.idFromObj(this.form.categories.value);

      Object.entries(this.form).map(([k, v]) => (formValues[k] = v.value));

      if (this.campaign) {
        formValues.campaign_code = this.campaign;
      }

      if (this.isFreeTrial) {
        formValues.is_free_trial = true;
      }
      if (this.enterpriseInvitation) {
        formValues.enterprise_invitation = this.enterpriseInvitation;
      }

      this.companyId
        ? await this.updateCompanyData(formValues)
        : await this.setCompanyData(formValues);

      await this.setWizardComplete();
    },
    async updateCompanyProfile(data) {
      await this.$store.dispatch("company/setCompanyId", data.id);
      await this.$store.dispatch("company/fetchCompanies");
      const { company_subscription, user_id } = await serviceHandler(
        usersService.getUserDetails()
      );
      this.userId = user_id;
      this.subscriptionCreatedAt = company_subscription.created_at;
      this.$store.dispatch("auth/setPlanRestrictions", company_subscription);
      this.companyId = data.id;
      this.setCompany(data, false);
    },

    async updateCompanyData(formValues) {
      const data = await serviceHandler(
        companyService.updateCompany(formValues, this.companyId)
      );

      if (data) {
        await this.updateCompanyProfile(data);
      }
    },

    async setCompanyData(formValues) {
      const data = await serviceHandler(
        companyService.postCompany({
          ...formValues
        })
      );
      if (data) {
        await this.updateCompanyProfile(data);
      }
    },
    async fetchCompanyProfileData() {
      const data = await serviceHandler(companyService.getCompanies());
      await this.$store.dispatch("company/setCompanies", data);

      if (!data.length) return;

      const responseData = data.find(
        company =>
          company.company_user.type === AccountType.OWNER &&
          !company.wizard_completed
      );

      if (!responseData) return;

      if (this.$route.query.action === "add") {
        this.companyId = null;
      } else {
        this.companyId = responseData.id;
        await this.$store.dispatch("company/setCompany", {
          company: responseData
        });

        Object.entries(responseData).map(([k, v]) =>
          this.form[k] ? (this.form[k].value = v) : false
        );
      }
    },
    updateIntercom(time) {
      setTimeout(() => {
        if ("Intercom" in window) {
          window.Intercom("update");
        }
      }, time);
    },
    async setCompanies() {
      await this.$store.dispatch("company/fetchCompanies");
      this.setCompany(this.$store.state.company.companies.at(-1));
    },
    async setWizardComplete() {
      await this.$store.dispatch("company/setWizardCompletionStatus", true);
      this.storeAnalyticsData();
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.WIZARD_COMPLETE,
        action: "finished",
        last_step: 3
      });
      if (this.initialPlanPayload) {
        await this.postInitialChosenPlan(this.initialPlanPayload);
      }
      if (this.isFreeTrial) {
        this.updateIntercom(5000);
        this.updateIntercom(300000);

        this.sendSignUpEvent();

        await this.$router.push({
          name: "profile-gmb-accounts",
          query: {
            subscription_type: "automatic_subscription",
            ...(this.campaign && { acquisition_campaign: this.campaign }),
            ...(this.isEnLanguage && { lang: "en" })
          }
        });
      } else if (this.enterpriseInvitation) {
        this.sendSignUpEvent();

        await this.$router.push({
          name: "profile-gmb-accounts",
          query: {
            enterprise_invitation: this.enterpriseInvitation,
            ...(this.isEnLanguage && { lang: "en" })
          }
        });
      } else {
        this.sendSignUpEvent();

        await this.$router.push({
          name: "order-details",
          query: this.$route.query
        });
      }
    },
    storeAnalyticsData() {
      localStorage.setItem(LOCAL_STORAGE_KEYS.CLIENT_ID, this.getClientId());
      localStorage.setItem(LOCAL_STORAGE_KEYS.SESSION_ID, this.getSessionId());
    },
    getClientId() {
      const clientId = getCookieValue("_ga");
      const parts = clientId.split(".");
      return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : "";
    },
    getSessionId() {
      const sessionId =
        getCookieValue(`_ga_${process.env.VUE_APP_MEASUREMENT_ID}`) || "";
      const match = sessionId.match(/s(\d+)/);
      return match ? match[1] : "";
    },
    sendSignUpEvent() {
      const totalPrice = this.$route.query.price_total;
      const currency = this.$route.query.currency;

      window.dataLayer.push({
        event: "sign_up",
        user_id: this.userId,
        user_type: checkUserType(this.$store.state.auth.user.email),
        license_created_at: moment(this.subscriptionCreatedAt).unix(),
        plan: this.initialPlanPayload.product_name,
        size: this.initialPlanPayload.quantity,
        payment_cycle:
          this.initialPlanPayload.period === 1 ? "monthly" : "yearly",
        ...(totalPrice && {
          total_price: parseInt(totalPrice),
          currency: currency.toUpperCase()
        })
      });
    }
  },
  watch: {
    async isEnLanguage() {
      this.$store.commit("company/SET_LOADING", true);
      await this.fetchCountries();
      this.$store.commit("company/SET_LOADING", false);
    }
  }
};
</script>

<style lang="scss" scoped>
.wizard__tax-number-container {
  max-width: 355px;
}

.wizard__tax-number-container p {
  font-style: normal;
  font-weight: $font-weight-normal;
  line-height: 22px;
  align-items: center;
  text-align: center;
}

.wizard__tax-number-field {
  max-width: 300px;
}

.wizard__tax-number-loader {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
}

.trial-info {
  @include font-size(12px);
  padding: $spacing * 2 $spacing;
}
</style>
